import Icon from "@imminently/decisively-ui/Icon";
import * as React from "react";
import { useState } from "react";
import TextInput from "@imminently/decisively-ui/TextInput";
import TextField from "@imminently/decisively-ui/TextField";
import TextArea from "@imminently/decisively-ui/TextArea";
import { Label } from "@/ui/primitives/label";
import Select from "@imminently/decisively-ui/Select";
import Switch from "@imminently/decisively-ui/Switch";
import Checkbox from "@imminently/decisively-ui/Checkbox";
import Button from "@imminently/decisively-ui/Button";

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage";

type InputType = "text" | "email" | "password" | "number" | "search";

export function InputPage() {
  const [type, setType] = useState<InputType>("text");
  const [placeholder, setPlaceholder] = useState("Enter text...");
  const [disabled, setDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [numberValue, setNumberValue] = useState("12");

  return (
    <ComponentPage
      title="Input"
      description="Displays a form input field or a component that looks like an input field."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with input configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <div className="w-full max-w-sm">
              <TextInput
                type={type === "password" && showPassword ? "text" : type}
                placeholder={placeholder}
                disabled={disabled}
                fullWidth
              />
            </div>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Type">
              <Select
                value={type}
                onValueChange={(v) => setType((v ?? "text") as InputType)}
                options={[
                  { value: "text", label: "Text" },
                  { value: "email", label: "Email" },
                  { value: "password", label: "Password" },
                  { value: "number", label: "Number" },
                  { value: "search", label: "Search" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Placeholder">
              <TextInput
                value={placeholder}
                onValueChange={(v) => setPlaceholder(v ?? "")}
                fullWidth
              />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch
                checked={disabled}
                onCheckedChange={setDisabled}
              />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* Default */}
      <ComponentSection
        title="Default"
        description="Basic input field"
      >
        <ComponentPreview>
          <div className="w-full max-w-sm">
            <TextInput placeholder="Enter your name" />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Width"
        description="Inputs are intrinsic by default and opt into container width."
      >
        <ComponentPreview className="flex-col items-start gap-4">
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Intrinsic</Label>
            <TextInput placeholder="Intrinsic input" />
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Full width</Label>
            <TextInput
              placeholder="Full-width input"
              fullWidth
            />
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <TextField
              name="intrinsicField"
              label="Intrinsic field"
              placeholder="Intrinsic field"
            />
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <TextField
              name="fullWidthField"
              label="Full-width field"
              placeholder="Full-width field"
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="TextArea"
        description="Multi-line text input with intrinsic and full-width sizing."
      >
        <ComponentPreview className="flex-col items-start gap-4">
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Intrinsic textarea</Label>
            <TextArea placeholder="Write a short note..." />
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Full-width textarea</Label>
            <TextArea
              placeholder="Write a longer description..."
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="TextField + TextArea"
        description="Field-style text input paired with a multi-line textarea control."
      >
        <ComponentPreview>
          <div className="grid w-full max-w-sm gap-3">
            <TextField
              name="headlineField"
              label="Headline"
              placeholder="Enter a headline"
              fullWidth
            />
            <div className="grid gap-1.5">
              <Label>Body</Label>
              <TextArea
                placeholder="Enter detailed body text..."
                fullWidth
              />
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* With Label */}
      <ComponentSection
        title="With Label"
        description="Input with associated label"
      >
        <ComponentPreview>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="email">Email</Label>
            <TextInput
              type="email"
              id="email"
              placeholder="Email"
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Input Types */}
      <ComponentSection
        title="Input Types"
        description="Different input types"
      >
        <ComponentPreview className="flex-col gap-4 items-stretch max-w-sm w-full">
          <div className="grid gap-1.5">
            <Label htmlFor="text-input">Text</Label>
            <TextInput
              type="text"
              id="text-input"
              placeholder="Enter text"
              fullWidth
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="email-input">Email</Label>
            <TextInput
              type="email"
              id="email-input"
              placeholder="email@example.com"
              fullWidth
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="password-input">Password</Label>
            <TextInput
              type="password"
              id="password-input"
              placeholder="••••••••"
              fullWidth
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="number-input">Number</Label>
            <TextInput
              type="number"
              id="number-input"
              placeholder="0"
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Number Input"
        description="Number input uses the dedicated stepped input with icon buttons and keyboard/wheel stepping."
      >
        <ComponentPreview className="flex-col gap-3 items-stretch max-w-sm w-full">
          <div className="grid gap-1.5">
            <Label htmlFor="number-input-stepped">Quantity</Label>
            <TextInput
              type="number"
              id="number-input-stepped"
              value={numberValue}
              onValueChange={(next) => setNumberValue(next ?? "")}
              min={0}
              max={100}
              step={5}
              fullWidth
            />
          </div>
          <p className="text-sm text-muted-foreground">Try Arrow Up/Down, mouse wheel, or +/- buttons.</p>
        </ComponentPreview>
      </ComponentSection>

      {/* With Icon */}
      <ComponentSection
        title="With Icon"
        description="Input with icons"
      >
        <ComponentPreview className="flex-col gap-4 items-stretch max-w-sm w-full">
          <div className="relative">
            <Icon
              name="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            />
            <TextInput
              placeholder="Search..."
              className="pl-9"
            />
          </div>
          <div className="relative">
            <Icon
              name="email"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            />
            <TextInput
              type="email"
              placeholder="Email"
              className="pl-9"
            />
          </div>
          <div className="relative">
            <TextInput
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className="pr-9"
              fullWidth
            />
            <Button
              label={showPassword ? "Hide password" : "Show password"}
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <Icon
                  name="hide"
                  className="h-4 w-4 text-muted-foreground"
                />
              ) : (
                <Icon
                  name="visibility"
                  className="h-4 w-4 text-muted-foreground"
                />
              )}
            </Button>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Adornments"
        description="InputBox adornment layout with intrinsic and full-width sizing."
      >
        <ComponentPreview className="flex-col items-start gap-4">
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Intrinsic start adornment</Label>
            <TextInput
              placeholder="Search"
              startAdornment={
                <Icon
                  name="search"
                  className="h-4 w-4"
                />
              }
            />
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Full-width start adornment</Label>
            <TextInput
              placeholder="Search"
              startAdornment={
                <Icon
                  name="search"
                  className="h-4 w-4"
                />
              }
              fullWidth
            />
          </div>
          <div className="grid w-full max-w-sm gap-1.5">
            <Label>Full-width end adornment</Label>
            <TextInput
              placeholder="Amount"
              endAdornment={<span className="text-xs">USD</span>}
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* States */}
      <ComponentSection
        title="States"
        description="Different input states"
      >
        <ComponentPreview className="flex-col gap-4 items-stretch max-w-sm w-full">
          <div className="grid gap-1.5">
            <Label>Default</Label>
            <TextInput
              placeholder="Default state"
              fullWidth
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Disabled</Label>
            <TextInput
              placeholder="Disabled state"
              disabled
              fullWidth
            />
          </div>
          <div className="grid gap-1.5">
            <Label>With Value</Label>
            <TextInput
              value="Hello World"
              readOnly
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Disabled Controls"
        description="All disabled input variants grouped together for styling checks."
      >
        <ComponentPreview className="flex-col items-stretch gap-4 max-w-5xl w-full">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label>Text Input</Label>
              <TextInput
                placeholder="Disabled text"
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Text Field</Label>
              <TextField
                name="disabledTextField"
                label="Disabled field"
                placeholder="Disabled field"
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Text Area</Label>
              <TextArea
                placeholder="Disabled textarea"
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Number Input</Label>
              <TextInput
                type="number"
                value="10"
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>With Start Adornment</Label>
              <TextInput
                placeholder="Disabled with icon"
                startAdornment={
                  <Icon
                    name="search"
                    className="h-4 w-4"
                  />
                }
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>With End Adornment</Label>
              <TextInput
                placeholder="Disabled with suffix"
                endAdornment={<span className="text-xs">USD</span>}
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Password</Label>
              <TextInput
                type="password"
                placeholder="Disabled password"
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>File Input</Label>
              <TextInput
                type="file"
                disabled
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Switch</Label>
              <div className="pt-2">
                <Switch
                  checked
                  disabled
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Checkbox</Label>
              <div className="pt-2">
                <Checkbox disabled />
              </div>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* File Input */}
      <ComponentSection
        title="File Input"
        description="Input for file uploads"
      >
        <ComponentPreview>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="file">Upload file</Label>
            <TextInput
              id="file"
              type="file"
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Form Example */}
      <ComponentSection
        title="Form Example"
        description="Input in a form context"
      >
        <ComponentPreview>
          <form className="w-full max-w-sm space-y-4">
            <div className="grid gap-1.5">
              <Label htmlFor="form-name">Full Name</Label>
              <TextInput
                id="form-name"
                placeholder="John Doe"
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="form-email">Email</Label>
              <TextInput
                id="form-email"
                type="email"
                placeholder="john@example.com"
                fullWidth
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="form-password">Password</Label>
              <TextInput
                id="form-password"
                type="password"
                placeholder="••••••••"
                fullWidth
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              label="Sign Up"
            >
              Sign Up
            </Button>
          </form>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
